import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import * as Headless from '@headlessui/react';
import { Text } from './text';
import { twMerge } from 'tailwind-merge';
import { useNoScrollAnchoring } from './use-no-scroll-anchoring';

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
  '8xl': 'sm:max-w-8xl',
  '9xl': 'sm:max-w-9xl',
  '10xl': 'sm:max-w-10xl'
};

export function MountableDialog({
  size = 'lg',
  className,
  children,
  ...props
}: { size?: keyof typeof sizes; className?: string; children: React.ReactNode } & Omit<
  Headless.DialogProps,
  'className'
>) {
  useNoScrollAnchoring(true);

  return (
    <Headless.Dialog static open {...props} className="relative z-50">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1, ease: 'easeInOut' }}
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-neutral-950/75 focus:outline-0"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <Headless.DialogPanel as={React.Fragment}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              className={clsx(
                className,
                sizes[size],
                'row-start-2 w-full min-w-0 rounded-t-3xl bg-white p-[--gutter] shadow-lg ring-1 ring-neutral-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl dark:bg-neutral-900 dark:ring-white/10 forced-colors:outline'
              )}
            >
              {children}
            </motion.div>
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}

export function Dialog({
  size = 'lg',
  className,
  children,
  ...props
}: { size?: keyof typeof sizes; className?: string; children: React.ReactNode } & Omit<
  Headless.DialogProps,
  'className'
>) {
  useNoScrollAnchoring(!!props.open);

  return (
    <Headless.Dialog {...props} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-neutral-950/75"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <Headless.DialogPanel
            transition
            className={clsx(
              className,
              sizes[size],
              'row-start-2 w-full min-w-0 rounded-t-3xl bg-white p-[--gutter] shadow-lg ring-1 ring-neutral-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl dark:bg-neutral-900 dark:ring-white/10 forced-colors:outline',
              'transition duration-100 data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95'
            )}
          >
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}

export function BottomSheetDialog({
  size = 'lg',
  className,
  children,
  ...props
}: { size?: keyof typeof sizes; className?: string; children: React.ReactNode } & Omit<
  Headless.DialogProps,
  'className'
>) {
  useNoScrollAnchoring(!!props.open);

  return (
    <Headless.Dialog {...props} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in dark:bg-neutral-950/75"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center">
          <Headless.DialogPanel
            transition
            className={clsx(
              className,
              sizes[size],
              'row-start-2 w-full min-w-0 rounded-t-3xl bg-white p-[--gutter] shadow-lg ring-1 ring-neutral-950/10 [--gutter:theme(spacing.8)] dark:bg-neutral-900 dark:ring-white/10 forced-colors:outline',
              'transition duration-100 data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in'
            )}
          >
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}

export function DialogTitle({
  className,
  ...props
}: { className?: string } & Omit<Headless.DialogTitleProps, 'className'>) {
  return (
    <Headless.DialogTitle
      {...props}
      className={twMerge(
        'text-balance font-semibold text-neutral-950 text-lg/6 dark:text-white',
        className
      )}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps<typeof Text>, 'className'>) {
  return (
    <Headless.Description
      {...props}
      data-slot="text"
      className={clsx(
        className,
        'mt-2 text-pretty text-base/6 text-neutral-500 sm:text-sm/6 dark:text-neutral-400'
      )}
    />
  );
}

export function DialogBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={twMerge(className, 'mt-6')} />;
}

export function DialogActions({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'mt-8 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto'
      )}
    />
  );
}

/**
 * Footer for settings that apply to the whole form (account, visibility), not
 * one field. On a narrow screen the clusters stack with actions first so the
 * primary click stays in the thumb zone. Side by side, the settings cluster
 * gives up width first, so an action's label never wraps onto a second line.
 */
export function DialogFooter({
  className,
  actions,
  children,
  ...props
}: { actions?: React.ReactNode } & React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'mt-8 flex flex-col-reverse gap-4 border-t border-neutral-950/10 pt-5',
        'sm:flex-row sm:items-center sm:justify-between dark:border-white/10'
      )}
    >
      {/* Nudged left by a trigger's own horizontal padding, so the first control's
          text lines up with the dialog body above it rather than sitting inset. */}
      <div className="-ml-2.5 flex min-w-0 flex-wrap items-center gap-1">{children}</div>
      {actions && (
        <div className="flex flex-col-reverse items-center gap-3 *:w-full sm:shrink-0 sm:flex-row sm:*:w-auto">
          {actions}
        </div>
      )}
    </div>
  );
}
